import React from "react";
import { Box, CardContent, Typography } from "@mui/material";
import { PieChart as PieChartIcon } from "@mui/icons-material";
import { StandardCard } from "../../../../components/Cards";
import { Chart } from "../../../marketingCampaign/components/Graphs/chart";
import { BarGraphSkeleton } from "../skeletons";
import {
  IUpsellingDataItem,
  IVenueCard,
  isVenueCard,
} from "../../types/upsell.types";
import { VENUE_PERFORMANCE_DONUT_OPTIONS } from "../../constants/chartOptions";
import { NoRecordFound } from "../../../../components/NoRecordFound/NoRecordFound";

interface VenuePerformanceDonutGraphProps {
  dashboardData: IUpsellingDataItem[] | null;
  height?: number | string;
}

export const VenuePerformanceDonutGraph: React.FC<
  VenuePerformanceDonutGraphProps
> = ({ dashboardData, height }) => {
  if (!dashboardData || dashboardData.length === 0) {
    return <BarGraphSkeleton />;
  }

  // Filter only venues using type guard
  const venues = dashboardData.filter(isVenueCard) as IVenueCard[];

  const venueData = venues.map((venue) => ({
    name: venue.venue_name,
    leads: venue.leads?.length || 0,
  }));

  // Filter out items with 0 leads to avoid clutter
  const nonZeroVenues = venueData.filter((venue) => venue.leads > 0);

  const chartData = {
    series: nonZeroVenues.map((venue) => venue.leads),
    labels: nonZeroVenues.map((venue) => venue.name),
  };

  const chartOptions = {
    ...VENUE_PERFORMANCE_DONUT_OPTIONS,
    labels: chartData.labels,
  };

  return (
    <StandardCard>
      <CardContent sx={{ p: height ? 2 : 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <PieChartIcon sx={{ mr: 1, color: "primary.main" }} />
          <Typography variant="h5" fontWeight={600}>
            Venue Performance
          </Typography>
        </Box>

        <Box sx={{ height: height ?? 400, mt: 1 }}>
          {venues.length === 0 ? (
            <NoRecordFound />
          ) : (
            <Chart
              type="donut"
              series={chartData.series}
              options={chartOptions}
              height={height ?? 500}
            />
          )}
        </Box>
      </CardContent>
    </StandardCard>
  );
};
